/// <amd-module name="Build/Common/Commands/savedefinition/Command" />
import * as React from "react";

import { BuildDefinition, BuildDefinitionTemplate, DefinitionQuality, Folder } from "Build/Client/Contracts/Build";
import { IBuildRestClient } from "Build/Client/RestClient/Build";
import { DisallowedCharacters } from "Build/Common/Commands/Constants";
import { IFolderIdentifier, ISaveDefinitionProps, ISaveDefinitionState } from "Build/Common/Commands/savedefinition/Command.types";
import { handleError } from "Build/Common/Library/ErrorHandler";

import { Button } from "VSSUI/Button";
import { Dialog } from "VSSUI/Dialog";
import { FormItem } from "VSSUI/FormItem";
import { TitleSize } from "VSSUI/Header";
import { TextField } from "VSSUI/TextField";

import { endsWith, format } from "VSS/Core/Util/String";
import { LegacyComponent } from "VSS/Legacy/Legacy";
import { IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";

import * as Resources from "Build/Common/Commands/Resources";

import { IFolderManageDialogProps, IFolderManageDialogResult } from "./Types";

class SaveDefinition extends VssComponent<ISaveDefinitionProps, ISaveDefinitionState> {
    public static componentType = "ciSaveDefinitionComponent";

    constructor(props: ISaveDefinitionProps) {
        super(props);

        let name = "";
        let path = "";

        if (props.definition) {
            name = props.definition.name;
            path = props.definition.path;
        } else if (props.folder) {
            name = this._getName(props.folder);
            path = this._getParentPath(props.folder);
        }

        this.state = {
            name: name,
            definitionPath: path,
            description: "",
            nameErrorMessage: "",
            pathErrorMessage: "",
            showFolderDialog: false
        };
    }

    public render() {
        let folderDialogProps: IFolderManageDialogProps = {
            title: Resources.SelectFolder,
            okManageDialogCallBack: this._onFolderDialogResult,
            onManageDialogDissmiss: this._onDismissFolderDialog,
            showDialogActions: false,
            defaultPath: this.state.definitionPath,
            showDialog: this.state.showFolderDialog
        };

        let dialogTitle = Resources.RenameDefinitionDialogTitle;
        if (this.props.saveAsTemplate) {
            dialogTitle = Resources.SaveAsTemplateDefinitionDialogTitle;
        } else if (!this.props.definition) {
            dialogTitle = Resources.RenameFolderDialogTitle;
        }

        return (
            <Dialog
                calloutContentClassName="ci-dialog"
                footerButtonProps={[
                    {
                        onClick: this._onDismiss,
                        text: Resources.Cancel
                    },
                    {
                        disabled: this.state.nameErrorMessage.length > 0 || this.state.pathErrorMessage.length > 0,
                        onClick: this._onCommit.bind(this, this.state.name, this.state.definitionPath, this.state.description),
                        primary: true,
                        text: Resources.Save
                    }
                ]}
                onDismiss={this._onDismiss}
                titleProps={{ text: dialogTitle, size: TitleSize.Large }}
            >
                <LegacyComponent
                    modules={["Build/Scripts/Components/FolderManageDialog"]}
                    wrappedType="ci-folder-manage-dialog"
                    {...folderDialogProps}
                />
                <FormItem
                    label={Resources.DefinitionNameTextboxLabel}
                    message={this.state.nameErrorMessage}
                    error={this.state.nameErrorMessage.length > 0}
                >
                    <TextField
                        value={this.state.name}
                        onChange={this._onNameChanged}
                        required={true}
                    />
                </FormItem>
                {!this.props.saveAsTemplate && this.props.definition && (
                    <div className="ci-folder-picker-container flex">
                        <FormItem
                            className="flex-grow"
                            label={Resources.DefinitionFolderTextboxLabel}
                            message={this.state.pathErrorMessage}
                            error={this.state.pathErrorMessage.length > 0}
                        >
                            <TextField
                                value={this.state.definitionPath}
                                onChange={this._onPathChanged}
                                required={true}
                            />
                        </FormItem>
                        <Button
                            subtle={true}
                            className="flex-self-end"
                            iconProps={{ iconName: "More" }}
                            disabled={false}
                            onClick={this._onShowFolderDialog}
                            ariaLabel={Resources.ChooseFolder}
                        />
                    </div>
                )}
            </Dialog>
        );
    }

    private _onCommit = (newName: string, newPath: string, description: string) => {
        this.props.onClose();

        const trimmedName = newName.trim();
        const trimmedPath = newPath.trim();

        let client = this.props.pageContext.getRestClient<IBuildRestClient>("IBuildRestClient");

        if (this.props.definition) {
            client.getDefinition(this.props.definition.project.id, this.props.definition.id).then((value: BuildDefinition) => {
                value.comment = ""; // do not preserve the comment for the previous revision
                if (this.props.saveAsTemplate) {
                    value.quality = DefinitionQuality.Definition;
                    let template = {
                        name: trimmedName,
                        description: description,
                        template: value
                    } as BuildDefinitionTemplate;
                    client
                        .saveTemplate(template, value.project.id, template.name.replace(/[^0-9a-zA-Z-_.]/g, ""))
                        .then((newTemplate: BuildDefinitionTemplate) => {
                            // Currently there is no user feedback for saving as a template, so no action is invoked on success
                        }, handleError);
                } else {
                    value.name = trimmedName;
                    value.description = description;
                    value.path = trimmedPath;
                    client.updateDefinition(value, value.project.id, value.id).then((updatedDefinition: BuildDefinition) => {
                        if (this.props.onDefinitionSaved) {
                            this.props.onDefinitionSaved(updatedDefinition);
                        }
                    }, handleError);
                }
            }, handleError);
        } else if (this.props.folder) {
            let folder = {
                path: trimmedPath + "\\" + trimmedName,
                description: description
            } as Folder;
            let previousPath = this.props.folder.path;

            client.updateFolder(folder, this.props.projectId, previousPath).then((updatedFolder: Folder) => {
                if (this.props.onFolderSaved) {
                    this.props.onFolderSaved(updatedFolder, previousPath);
                }
            }, handleError);
        }
    };

    private _onDismiss = () => {
        this.props.onClose();
    };

    private _onNameChanged = (event: React.ChangeEvent<HTMLInputElement>, newValue: string) => {
        let errorMessage = "";
        if (newValue.trim().length === 0) {
            errorMessage = Resources.NameCannotBeEmptyErrorMessage;
        } else {
            const invalidCharacters = this.props.definition ? DisallowedCharacters.DisallowedPipelineNameCharacters : DisallowedCharacters.DisallowedFolderNameCharacters;
            for (let i = 0; i < invalidCharacters.length; i++) {
                if (newValue.indexOf(invalidCharacters[i]) !== -1) {
                    errorMessage = format(Resources.UnallowedNameCharactersErrorMessage, invalidCharacters);
                }
            }
        }
        this.setState({
            name: newValue,
            nameErrorMessage: errorMessage
        });
    };

    private _onPathChanged = (event: React.ChangeEvent<HTMLInputElement>, newValue: string) => {
        let errorMessage = "";
        if (newValue.trim().length === 0) {
            errorMessage = Resources.FolderCannotBeEmptyErrorMessage;
        } else if (endsWith(newValue, ".")) {
            errorMessage = Resources.FolderCannotEndWithPeriodErrorMessage;
        } else {
            const invalidCharacters = DisallowedCharacters.DisallowedFolderPathCharacters;
            for (let i = 0; i < invalidCharacters.length; i++) {
                if (newValue.indexOf(invalidCharacters[i]) !== -1) {
                    errorMessage = format(Resources.UnallowedFolderCharactersErrorMessage, invalidCharacters);
                }
            }
        }
        this.setState({
            definitionPath: newValue,
            pathErrorMessage: errorMessage
        });
    };

    private _onShowFolderDialog = () => {
        this.setState({
            showFolderDialog: true
        });
    };

    private _onDismissFolderDialog = () => {
        this.setState({
            showFolderDialog: false
        });
    };

    private _onFolderDialogResult = (result: IFolderManageDialogResult) => {
        this.setState({
            definitionPath: result.path
        });
    };

    private _getName(folder: IFolderIdentifier): string {
        let lastSeparator = folder.path.lastIndexOf("\\");
        let name = folder.path.substring(lastSeparator + 1);
        return name;
    }

    private _getParentPath(folder: IFolderIdentifier): string {
        let lastSeparator = folder.path.lastIndexOf("\\");
        let parent = folder.path.substring(0, lastSeparator);
        return parent;
    }
}

VssComponent.register(SaveDefinition.componentType, SaveDefinition);
